/**
 * staleActiveOperation — diagnostics-only validation for continuity
 * resume attempts.
 *
 * Operator brief (Feb 19, 2026):
 *   "Validate stale continuity restoration before resume."
 *
 * What this module provides:
 *   • detectStaleResume(target, op) — pure: returns
 *     { stale: bool, reason }. `target` is a URL or path that the
 *     caller is about to navigate to. `op` is the shape returned by
 *     useActiveOperation().
 *   • notifyStaleResume({ navigate, role, intendedTarget }) — fires
 *     a `stale_active_operation` client-error report, navigates to
 *     the role-appropriate home, and sets a sessionStorage flag the
 *     home renderer reads to surface a calm slate banner with the
 *     ref ID.
 *
 * The actual navigation policy (which routes count as a "continuity
 * resume target") is intentionally narrow: today only Lesson Review
 * and the Active Operation pill deep-links carry resume semantics.
 * Add new patterns here when new continuity routes ship.
 */
import { reportClientError } from "./clientErrorReporter";


// Path prefixes whose deep-link nature means *staleness matters*. If
// the user lands here without an active operation backing it, the
// page often dead-ends. Validate freshness before resume.
export const RESUME_PATH_PREFIXES = Object.freeze([
  "/lesson-review/",
  "/work-mode/lesson/",
  "/ai-offer/",
]);


export function looksLikeContinuityResume(target) {
  if (!target || typeof target !== "string") return false;
  // Strip the origin if present.
  let path = target;
  try {
    const u = new URL(target, "http://x");
    path = u.pathname + (u.search || "");
  } catch (_) { /* not a URL — treat raw */ }
  return RESUME_PATH_PREFIXES.some((p) => path.startsWith(p))
    || /[?&](resume|continue)=1\b/.test(path);
}


/**
 * @returns {{ stale: boolean, reason: string|null }}
 */
export function detectStaleResume(target, op) {
  if (!looksLikeContinuityResume(target)) {
    return { stale: false, reason: null };
  }
  if (!op) {
    return { stale: true, reason: "no_active_operation_state" };
  }
  if (op.active === false) {
    return { stale: true, reason: "operation_not_active" };
  }
  return { stale: false, reason: null };
}


// ── Banner handoff via sessionStorage ─────────────────────────────
const SS_KEY = "ndg_stale_session_banner";

export function readStaleBanner() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(SS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

export function clearStaleBanner() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(SS_KEY);
  } catch (_) { /* noop */ }
}

function _writeStaleBanner(payload) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(SS_KEY, JSON.stringify(payload));
  } catch (_) { /* noop */ }
}


/**
 * Single-call helper for "I am about to resume something — if it's
 * stale, log it, route home calmly, surface the ref to the user."
 *
 * @param {object} args
 * @param {(path: string) => void} args.navigate — react-router nav fn
 * @param {string} args.role — "learner" | "adi" | "pdi" | "admin"
 * @param {string} args.intendedTarget — the URL/path the user tried to reach
 * @param {object} args.op — active operation snapshot
 * @returns {Promise<{ stale: boolean, refId?: string }>}
 */
export async function notifyStaleResume({
  navigate, role, intendedTarget, op,
}) {
  const verdict = detectStaleResume(intendedTarget, op);
  if (!verdict.stale) return { stale: false };

  const { ref_id } = await reportClientError({
    category: "stale_active_operation",
    extras: {
      intended_target: intendedTarget,
      reason: verdict.reason,
      active_operation_snapshot: op
        ? {
          active: op.active,
          kind: op.kind || null,
          operation_id: op.operation_id || null,
        }
        : null,
    },
  });

  _writeStaleBanner({
    ref_id,
    role: role || null,
    intended_target: intendedTarget,
    reason: verdict.reason,
    at: new Date().toISOString(),
  });

  const home = _roleHome(role);
  try {
    if (typeof navigate === "function") {
      navigate(home, { replace: true });
    } else if (typeof window !== "undefined") {
      window.location.assign(home);
    }
  } catch (_) { /* noop */ }

  return { stale: true, refId: ref_id };
}


function _roleHome(role) {
  switch (role) {
    case "adi":
    case "pdi":
      return "/instructor";
    case "admin":
      return "/admin";
    case "learner":
      return "/learn";
    default:
      return "/";
  }
}
